import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ClipboardCheck, Check, AlertTriangle, X } from 'lucide-react';
import FlashingProfileSVG from '@/components/FlashingProfileSVG';

const STATUS = [
  { key: 'ok', label: 'Correct', icon: Check, on: 'bg-emerald-600 text-white border-emerald-600' },
  { key: 'short', label: 'Short', icon: AlertTriangle, on: 'bg-amber-500 text-white border-amber-500' },
  { key: 'wrong', label: 'Wrong', icon: X, on: 'bg-red-600 text-white border-red-600' },
];

// Site check — crew tick each delivered flashing against the template, folds, girth, length and colour ordered.
export default function FlashingSiteCheck({ api, appraisalId }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/appraisals/${appraisalId}/flashing-check`).then(({ data: d }) => setData(d)).catch(() => {});
  }, [api, appraisalId]);
  useEffect(() => { load(); }, [load]);

  const save = async (item, patch) => {
    try {
      const { data: d } = await api.put(`/appraisals/${appraisalId}/flashing-check/${encodeURIComponent(item.key)}`,
        { status: item.status, note: item.note, ...patch });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the check'); }
  };

  if (!data || !data.items.length) return null;
  const s = data.summary;
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="flashing-site-check">
      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <span className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <ClipboardCheck size={15} className="text-orange-600" /> Flashing site check
        </span>
        <span className="text-[11px] text-slate-500" data-testid="flashing-site-check-summary">
          <b className="text-emerald-700">{s.ok}</b> correct · <b className="text-amber-700">{s.short}</b> short ·{' '}
          <b className="text-red-700">{s.wrong}</b> wrong · <b className="text-slate-700">{s.unchecked}</b> to check of {s.total}
        </span>
      </div>
      <div className="divide-y divide-slate-100">
        {data.items.map((it) => <CheckRow key={it.key} it={it} onSave={(p) => save(it, p)} />)}
      </div>
    </div>
  );
}

function CheckRow({ it, onSave }) {
  const [note, setNote] = useState(it.note);
  useEffect(() => setNote(it.note), [it.note]);
  return (
    <div className="px-4 py-3 flex flex-wrap gap-4 items-start" data-testid={`flashing-check-row-${it.ref}`}>
      <FlashingProfileSVG folds={it.folds} startAngle={it.start_angle_deg} width={300} height={150} labelsLayout="right-column" />
      <div className="flex-1 min-w-[220px] text-xs space-y-0.5">
        <div className="font-semibold text-slate-800"><span className="font-mono text-slate-500 mr-1.5">{it.ref}</span>{it.type_name || '—'}</div>
        <div className="text-slate-600">{it.spec || <span className="text-red-700 font-semibold">No template / folds recorded</span>}</div>
        <div className="text-slate-600">{it.length.toFixed(2)} m × {it.quantity || 1}</div>
        {it.checked_by && <div className="text-[10px] text-slate-400">Checked by {it.checked_by} · {new Date(it.checked_at).toLocaleString()}</div>}
      </div>
      <div className="space-y-1.5">
        <div className="flex gap-1">
          {STATUS.map(({ key, label, icon: Icon, on }) => (
            <button key={key} type="button" data-testid={`flashing-check-${key}-${it.ref}`}
              onClick={() => onSave({ status: it.status === key ? '' : key })}
              className={`flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border transition-colors ${it.status === key ? on : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
              <Icon size={11} /> {label}
            </button>
          ))}
        </div>
        <input data-testid={`flashing-check-note-${it.ref}`} value={note} placeholder="Note (e.g. 2 m short)"
          onChange={(e) => setNote(e.target.value)} onBlur={() => note !== it.note && onSave({ note })}
          className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
      </div>
    </div>
  );
}
